import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { Overlay } from "@fidget/common/components/overlay";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { Path } from "../../models/router";
import styles from "./status.css";
import { useStore } from "../../models";

import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { staticConfig } from "@fidget/common/config/static-config";
import { AppPermission, AppPresence } from "@closerplatform/spinner-openapi";
import { config } from "@fidget/common/config/config";
import { useWindowDimensions, WindowSize } from "../../models/WindowDimensions";

interface Props {
  settingsView: boolean;
}

export const Status = observer(({ settingsView }: Props) => {
  const { currentUserStore, uiStore } = useStore();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { windowSize } = useWindowDimensions();
  const [isCustomStatusesDropdownOpen, setIsCustomStatusesDropdownOpen] = useState<boolean>(false);
  const [widgetVisible, setWidgetVisible] = useState<boolean>(false);

  const areCallsDisabled = !!currentUserStore.orgConfig?.callsDisabled;
  const displayHelpLine = uiStore.canDisplayWithPermission(AppPermission.HelpPageVisibility);

  const shouldShowPreviewButton = () => {
    return uiStore.canDisplayAdminComponents() && !currentUserStore.isOauthEnabled;
  };

  const changeWidget = () => {
    const isVisible = !widgetVisible;
    if (currentUserStore.widgetEnabled) {
      const elem = document.getElementById("closer-widget");
      elem!.style.display = isVisible ? "block" : "none";
    } else {
      currentUserStore.enableWidget();
    }
    setWidgetVisible(isVisible);
  };

  const shouldShowUpgradePlanButton = () => {
    const isPlanCorrect =
      currentUserStore.planName !== staticConfig.beforeBillingPlan &&
      !(currentUserStore.planPrice > 0);

    return (
      uiStore.canDisplayAdminComponents() && isPlanCorrect && config.services.stripeScriptEnabled
    );
  };

  const changeUpgrade = () => {
    uiStore.changeUpgrade();
  };

  const renderStatusIndicator = (status: AppPresence) => {
    switch (status) {
      case AppPresence.Available:
        return <div className={`${styles["status-available"]} ${styles["status-indicator"]}`} />;
      case AppPresence.Away:
        return <div className={`${styles["status-away"]} ${styles["status-indicator"]}`} />;
      case AppPresence.Unavailable:
        return <div className={`${styles["status-unavailable"]} ${styles["status-indicator"]}`} />;
      case AppPresence.Unready:
        return <div className={`${styles["status-unready"]} ${styles["status-indicator"]}`} />;
      default:
        return <div />;
    }
  };

  const statusClicked = () => setIsOpen(!isOpen);

  const itemClicked = (status: AppPresence, unavailabilityReason?: string): void => {
    setIsOpen(false);
    setIsCustomStatusesDropdownOpen(false);
    currentUserStore.setPresence(status, true, unavailabilityReason);
  };

  const logout = () => currentUserStore.logout();

  const sendSupportMail = (): void => {
    window.location.href = `mailto:${staticConfig.closerSupportEmail}`;
  };

  const renderDropdown = () => {
    return (
      <div className={styles.list}>
        {renderStatusList()}
        <div className={styles.separator} />
        <Link
          to={Path.SETTINGS_PROFILE}
          className={styles.button}
          onClick={() => setIsOpen(false)}
          id={"settings"}
        >
          <span className={styles["status-title"]}>
            <L10n id={"settings.title"} />
          </span>
        </Link>

        {shouldShowUpgradePlanButton() && (
          <>
            <div className={styles.separator} />
            <button className={styles.button} onClick={changeUpgrade} id={"upgrade_plan_button"}>
              <span className={styles["status-title"]}>
                <L10n id={"header.tab.upgrade"} />
              </span>
            </button>
          </>
        )}

        {shouldShowPreviewButton() && (
          <>
            <div className={styles.separator} />
            <button className={styles.button} onClick={changeWidget} id={"preview_widget_button"}>
              <span className={styles["status-title"]}>
                {currentUserStore.widgetEnabled && widgetVisible ? (
                  <L10n id={"header.tab.previewWidget.hide"} />
                ) : (
                  <L10n id={"header.tab.previewWidget.show"} />
                )}
              </span>
            </button>
          </>
        )}
        {displayHelpLine && (
          <>
            <div className={styles.separator} />
            <button className={styles.button} onClick={sendSupportMail} id={"contact_us_button"}>
              <span className={styles["status-title"]}>
                <L10n id={"contact.title"} />
              </span>
            </button>
          </>
        )}
        <div className={styles.separator} />
        <button className={styles.button} onClick={logout} id={"sign_out_button"}>
          <span className={styles["status-title"]}>
            <L10n id={"login.signOut"} />
          </span>
        </button>
        <Overlay onClick={() => setIsOpen(false)} />
      </div>
    );
  };

  const renderCustomStatusDropdown = () => {
    const selected = currentUserStore.profile!.unavailabilityReason;
    return (
      <div>
        {currentUserStore.customReasons.map((customStatus) => (
          <button
            className={classNames(styles.button, styles["custom-status"], {
              [styles.selected]: customStatus.reasonSlug === selected
            })}
            key={customStatus.reasonSlug}
            onClick={() => itemClicked(AppPresence.Unavailable, customStatus.reasonSlug)}
          >
            {customStatus.reason}
            {renderStatusIndicator(AppPresence.Unavailable)}
          </button>
        ))}
      </div>
    );
  };

  const renderStatusList = () => {
    const { customReasons } = currentUserStore;

    const statuses: Array<AppPresence> = Object.values(AppPresence).filter(
      (status) =>
        !(
          (status === AppPresence.Unavailable && customReasons.length) ||
          (status === AppPresence.Away && areCallsDisabled)
        )
    );

    return (
      <>
        {statuses.map((status: AppPresence) => (
          <button
            key={status}
            className={styles.button}
            onClick={() => itemClicked(status)}
            id={status + "_status_button"}
          >
            <div className={styles["status-title"]}>
              {presenceStatusLabel(status, areCallsDisabled)}
            </div>
            <div className={styles["status-description"]}>
              {presenceStatusDescription(status, areCallsDisabled)}
            </div>
            {renderStatusIndicator(status)}
          </button>
        ))}
        {!!customReasons.length && (
          <>
            <button
              className={styles.button}
              onClick={() => setIsCustomStatusesDropdownOpen(!isCustomStatusesDropdownOpen)}
              id={AppPresence.Unavailable + "_status_button"}
            >
              <div className={styles["status-title"]}>
                {presenceStatusLabel(AppPresence.Unavailable, areCallsDisabled)}
                <i
                  className={classNames("icon-arrow-up", styles.arrow, {
                    [styles.rotate]: isCustomStatusesDropdownOpen
                  })}
                />
              </div>
              <div className={styles["status-description"]}>
                {presenceStatusDescription(AppPresence.Unavailable, areCallsDisabled)}
              </div>
            </button>
            {isCustomStatusesDropdownOpen && renderCustomStatusDropdown()}
          </>
        )}
      </>
    );
  };

  const render = () => {
    const { profile } = currentUserStore;
    return (
      <div className={styles["status-container"]}>
        <button
          className={classNames(styles.status, {
            [styles.active]: isOpen,
            [styles["settings-view"]]: settingsView
          })}
          onClick={statusClicked}
        >
          {renderStatusIndicator(profile!.presence)}

          <div className={styles.avatar}>
            <Avatar user={profile!} />
          </div>

          {windowSize !== WindowSize.Small && (
            <div className={styles["agent-name"]}>{profile!.firstName}</div>
          )}
        </button>
        {isOpen && renderDropdown()}
      </div>
    );
  };

  return render();
});

export const presenceStatusLabel = (status: AppPresence, callsDisabled: boolean) => {
  switch (status) {
    case AppPresence.Available:
      return intl.formatMessage(
        callsDisabled ? "presenceStatus.available.callsDisabled" : "presenceStatus.available"
      );
    case AppPresence.Away:
      return intl.formatMessage("presenceStatus.away");
    case AppPresence.Unready:
      return intl.formatMessage("presenceStatus.unready");
    case AppPresence.Unavailable:
      return intl.formatMessage("presenceStatus.unavailable");
    default:
      assertUnreachable(status);
      return "";
  }
};

export const presenceStatusDescription = (status: AppPresence, callsDisabled: boolean) => {
  switch (status) {
    case AppPresence.Available:
      return intl.formatMessage(
        callsDisabled
          ? "presenceStatus.available.description.callsDisabled"
          : "presenceStatus.available.description"
      );
    case AppPresence.Away:
      return intl.formatMessage("presenceStatus.away.description");
    case AppPresence.Unready:
      return intl.formatMessage("presenceStatus.unready.description");
    case AppPresence.Unavailable:
      return intl.formatMessage(
        callsDisabled
          ? "presenceStatus.unavailable.description.callsDisabled"
          : "presenceStatus.unavailable.description"
      );
    default:
      assertUnreachable(status);
      return "";
  }
};
